import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Search, Loader2, AlertCircle, Shield, ChevronDown } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { RcmColumnVisibilityMenu } from './RcmColumnVisibilityMenu.jsx';
import {
  createDefaultRcmItgcColumnVisibility,
  getDefaultRcmItgcColumnOrder,
  getHideableRcmItgcColumns,
  getOrderedVisibleRcmItgcColumnIds,
  getVisibleRcmItgcColumnCount,
  isRcmItgcColumnVisible,
  mergeRcmItgcHideableOrder,
  RCM_ITGC_TABLE_COLUMNS,
} from './rcmItgcTableColumnConfig.js';
import {
  clearStoredRcmColumnPreferences,
  readStoredRcmColumnOrder,
  readStoredRcmColumnVisibility,
  writeStoredRcmColumnOrder,
  writeStoredRcmColumnVisibility,
} from './rcmColumnPreferences.ts';
import { formatRcmClassificationLabel, formatRcmKeyReportsPreview } from './rcmAllRowMappers.js';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

const RCM_ITGC_COLUMN_IDS = RCM_ITGC_TABLE_COLUMNS.map((column) => column.id);

/**
 * ITGC scope of the RCM page. Sources directly from the system_itgc_controls
 * junction via GET /api/risk-control-mappings/itgc (one row per
 * system ↔ control ↔ risk link), joined to control + risk + system + domain.
 *
 * This is the table the RCM import writes ITGC rows into, so imported ITGC
 * controls (source='batch_import') show up here. Rendered in the Process
 * tab's column style (dark sticky header, bordered cells, horizontal scroll).
 *
 * Note: /api/systems/:id/linked-controls is NOT used — that endpoint only
 * surfaces seeded ITGC masters + org copies, not imported controls.
 */
function RcmItgcView({ onStatsChange, onFilteredRowsChange, onExportColumnsChange, reloadToken = 0 }) {
  const { selectedAuditYearId } = useAuditYear();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [domainFilter, setDomainFilter] = useState('all');
  const [systemFilter, setSystemFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [columnVisibility, setColumnVisibility] = useState(() =>
    readStoredRcmColumnVisibility('itgc', createDefaultRcmItgcColumnVisibility, RCM_ITGC_COLUMN_IDS)
  );
  const [columnOrder, setColumnOrder] = useState(() =>
    readStoredRcmColumnOrder('itgc', getDefaultRcmItgcColumnOrder, RCM_ITGC_COLUMN_IDS)
  );

  const visibleColumnCount = useMemo(
    () => getVisibleRcmItgcColumnCount(columnVisibility),
    [columnVisibility]
  );

  const orderedVisibleColumnIds = useMemo(
    () => getOrderedVisibleRcmItgcColumnIds(columnOrder, columnVisibility),
    [columnOrder, columnVisibility]
  );

  const handleToggleColumn = useCallback((columnId) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !isRcmItgcColumnVisible(prev, columnId),
    }));
  }, []);

  const handleReorderColumns = useCallback((orderedHideableIds) => {
    setColumnOrder((prev) => mergeRcmItgcHideableOrder(prev, orderedHideableIds));
  }, []);

  const handleResetColumns = useCallback(() => {
    clearStoredRcmColumnPreferences('itgc');
    setColumnVisibility(createDefaultRcmItgcColumnVisibility());
    setColumnOrder(getDefaultRcmItgcColumnOrder());
  }, []);

  useEffect(() => {
    writeStoredRcmColumnVisibility('itgc', columnVisibility);
  }, [columnVisibility]);

  useEffect(() => {
    writeStoredRcmColumnOrder('itgc', columnOrder);
  }, [columnOrder]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const params = {};
        if (selectedAuditYearId) params.audit_year_id = selectedAuditYearId;
        const res = await axiosInstance.get('/api/risk-control-mappings/itgc', { params });
        if (cancelled) return;
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        setRows(items);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.error || 'Failed to load ITGC RCM data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAuditYearId, reloadToken]);

  // Distinct domains / systems for the filter dropdowns.
  const domainOptions = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.domainCode) set.add(r.domainCode);
    return Array.from(set).sort();
  }, [rows]);

  const systemOptions = useMemo(() => {
    const map = new Map();
    for (const r of rows) if (r.systemId) map.set(r.systemId, r.systemName || r.systemId);
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [rows]);

  // Apply filters in-memory — rows are small (per-org ITGC link count).
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (domainFilter !== 'all' && r.domainCode !== domainFilter) return false;
      if (systemFilter !== 'all' && r.systemId !== systemFilter) return false;
      if (q) {
        const hay = `${r.controlId} ${r.controlObjective} ${r.controlDescription} ${r.riskId} ${r.riskDescription} ${r.systemName}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, domainFilter, systemFilter, search]);

  const stats = useMemo(() => ({
    rcmRows: rows.length,
    domains: new Set(rows.map((r) => r.domainCode).filter(Boolean)).size,
    keyControls: rows.filter((r) => (r.significance || '').toLowerCase() === 'key').length,
    distinctSystems: new Set(rows.map((r) => r.systemId).filter(Boolean)).size,
  }), [rows]);

  // Surface the stats to the page so the widget row above the scope tabs can
  // render them. Skipped while the initial fetch is in flight.
  const onStatsChangeRef = useRef(onStatsChange);
  useEffect(() => { onStatsChangeRef.current = onStatsChange; }, [onStatsChange]);
  useEffect(() => {
    if (loading) return;
    onStatsChangeRef.current?.(stats);
  }, [stats, loading]);

  // Keep parent export in sync with the filtered table (same rows as on screen / all filtered pages).
  const onFilteredRowsChangeRef = useRef(onFilteredRowsChange);
  useEffect(() => { onFilteredRowsChangeRef.current = onFilteredRowsChange; }, [onFilteredRowsChange]);
  useEffect(() => {
    if (loading) return;
    onFilteredRowsChangeRef.current?.(visibleRows);
  }, [visibleRows, loading]);

  // Keep parent Excel export aligned with gear column visibility + order.
  const onExportColumnsChangeRef = useRef(onExportColumnsChange);
  useEffect(() => { onExportColumnsChangeRef.current = onExportColumnsChange; }, [onExportColumnsChange]);
  useEffect(() => {
    onExportColumnsChangeRef.current?.(orderedVisibleColumnIds);
  }, [orderedVisibleColumnIds]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
          <p className="text-sm text-gray-500">Loading ITGC RCM…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
        <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
        <p className="text-sm text-red-800">{loadError}</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-10 text-center">
        <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
          <Shield className="h-8 w-8 text-teal-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">No ITGC controls yet</h2>
        <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
          ITGC rows appear here once controls are linked to systems — either by importing an RCM with
          ITGC rows, or by attaching standard controls on a system&rsquo;s
          <em> IT General Controls</em> tab.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      {/* Filter card */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shrink-0">
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 flex-1 min-w-0">
            <FilterField label="ITGC domain" hint="(e.g. AC, CH)">
              <Select value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)}>
                <option value="all">All domains</option>
                {domainOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </Select>
            </FilterField>
            <FilterField label="System" hint="(applications in scope)">
              <Select value={systemFilter} onChange={(e) => setSystemFilter(e.target.value)}>
                <option value="all">All systems</option>
                {systemOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </Select>
            </FilterField>
            <FilterField label="Search">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="control, system, risk, keyword…"
                  className="w-full pl-8 pr-2 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </FilterField>
          </div>
          <div className="shrink-0 pb-0.5">
            <RcmColumnVisibilityMenu
              columns={getHideableRcmItgcColumns()}
              columnVisibility={columnVisibility}
              onToggleColumn={handleToggleColumn}
              onReset={handleResetColumns}
              enableReorder
              columnOrder={columnOrder}
              onReorder={handleReorderColumns}
            />
          </div>
        </div>
        {(domainFilter !== 'all' || systemFilter !== 'all' || search) && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => { setDomainFilter('all'); setSystemFilter('all'); setSearch(''); }}
              className="text-xs font-medium text-teal-700 hover:text-teal-900"
            >
              Reset filters
            </button>
          </div>
        )}
      </div>

      {/* Table — column-style matrix matching the Process tab. */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="relative flex-1 min-h-0 overflow-x-auto overflow-y-auto">
          <table className="min-w-[1200px] w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-30 shadow-md">
              <tr className="bg-gray-900 text-white">
                {orderedVisibleColumnIds.map((columnId) => {
                  switch (columnId) {
                    case 'srNo':
                      return <Th key={columnId}>Sr. No.</Th>;
                    case 'classification':
                      return <Th key={columnId}>Classification</Th>;
                    case 'domain':
                      return <Th key={columnId}>Domain</Th>;
                    case 'system':
                      return <Th key={columnId} style={{ minWidth: '160px' }}>System</Th>;
                    case 'controlId':
                      return <Th key={columnId} style={{ minWidth: '120px' }}>Control ID</Th>;
                    case 'riskId':
                      return <Th key={columnId} style={{ minWidth: '120px' }}>Risk ID</Th>;
                    case 'riskDescription':
                      return <Th key={columnId} style={{ minWidth: '240px' }}>Risk Description</Th>;
                    case 'controlObjective':
                      return <Th key={columnId} style={{ minWidth: '220px' }}>Control Objective</Th>;
                    case 'controlDescription':
                      return <Th key={columnId} style={{ minWidth: '260px' }}>Control Description</Th>;
                    case 'controlOwner':
                      return <Th key={columnId} style={{ minWidth: '120px' }}>Control Owner</Th>;
                    case 'keyReports':
                      return <Th key={columnId} style={{ minWidth: '140px' }}>Key Reports</Th>;
                    case 'frequency':
                      return <Th key={columnId} style={{ minWidth: '100px' }}>Frequency</Th>;
                    case 'controlType':
                      return <Th key={columnId} className="text-center">Control Type</Th>;
                    case 'keyNonKey':
                      return <Th key={columnId} className="text-center">Key / Non Key</Th>;
                    default:
                      return null;
                  }
                })}
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(visibleColumnCount, 1)} className="px-6 py-10 text-center text-sm text-gray-500">
                    No rows match the current filters.
                  </td>
                </tr>
              ) : visibleRows.map((row, idx) => {
                const isKey = (row.significance || '').toLowerCase() === 'key';
                return (
                  <tr
                    key={row.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  >
                    {orderedVisibleColumnIds.map((columnId) => {
                      switch (columnId) {
                        case 'srNo':
                          return (
                            <Td key={columnId} className="text-center text-gray-600 font-medium">{idx + 1}</Td>
                          );
                        case 'classification':
                          return (
                            <Td key={columnId}>
                              <span className="inline-flex items-center rounded bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                                {formatRcmClassificationLabel(row.classification || 'itgc')}
                              </span>
                            </Td>
                          );
                        case 'domain':
                          return (
                            <Td key={columnId}>
                              <span className="inline-flex items-center rounded bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700">
                                {row.domainCode || '—'}
                              </span>
                            </Td>
                          );
                        case 'system':
                          return (
                            <Td key={columnId} title={row.systemName}>
                              <span className="line-clamp-2 text-indigo-700 font-medium">{row.systemName || '—'}</span>
                            </Td>
                          );
                        case 'controlId':
                          return (
                            <Td key={columnId} className="font-mono text-blue-700">{row.controlId || '—'}</Td>
                          );
                        case 'riskId':
                          return (
                            <Td key={columnId} className="font-mono text-gray-600">{row.riskId || '—'}</Td>
                          );
                        case 'riskDescription':
                          return (
                            <Td key={columnId} title={row.riskDescription}>
                              <span className="line-clamp-3 text-gray-700">{row.riskDescription || '—'}</span>
                            </Td>
                          );
                        case 'controlObjective':
                          return (
                            <Td key={columnId} title={row.controlObjective}>
                              <span className="line-clamp-3 font-medium text-gray-900">{row.controlObjective || '—'}</span>
                            </Td>
                          );
                        case 'controlDescription':
                          return (
                            <Td key={columnId} title={row.controlDescription}>
                              <span className="line-clamp-3 text-gray-500">{row.controlDescription || '—'}</span>
                            </Td>
                          );
                        case 'controlOwner':
                          return (
                            <Td key={columnId} title={row.controlOwner || undefined}>
                              <span className="text-gray-700">{row.controlOwner || '—'}</span>
                            </Td>
                          );
                        case 'keyReports': {
                          const { display, fullLabel, isCompact } = formatRcmKeyReportsPreview(
                            row.keyReports ?? row.key_reports
                          );
                          return (
                            <Td key={columnId} title={isCompact ? fullLabel : undefined}>
                              <span className={display === '-' ? 'text-gray-400' : 'text-gray-700'}>
                                {display === '-' ? '—' : display}
                              </span>
                            </Td>
                          );
                        }
                        case 'frequency':
                          return (
                            <Td key={columnId} className="whitespace-nowrap text-gray-600 capitalize">{row.frequency || '—'}</Td>
                          );
                        case 'controlType':
                          return (
                            <Td key={columnId} className="text-center whitespace-nowrap text-gray-700 capitalize">{row.controlType || '—'}</Td>
                          );
                        case 'keyNonKey':
                          return (
                            <Td key={columnId} className="text-center">
                              <span
                                className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
                                  isKey ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {isKey ? 'Key' : 'Non-Key'}
                              </span>
                            </Td>
                          );
                        default:
                          return null;
                      }
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
        {label} {hint ? <span className="ml-1 text-gray-400 font-normal normal-case">{hint}</span> : null}
      </label>
      {children}
    </div>
  );
}

function Select({ children, ...props }) {
  return (
    <div className="relative">
      <select
        {...props}
        className="w-full appearance-none pl-3 pr-8 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
      >
        {children}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
    </div>
  );
}

function Th({ children, className = '', style }) {
  return (
    <th
      className={`px-2.5 py-2 text-[11px] leading-tight font-semibold text-left whitespace-nowrap border-r border-gray-700 ${className}`}
      style={style}
    >
      {children}
    </th>
  );
}

function Td({ children, className = '', title }) {
  return (
    <td className="px-2.5 py-2 border-r border-gray-100 align-top" title={title}>
      <div className={`text-[11px] leading-tight ${className}`}>{children}</div>
    </td>
  );
}

export default RcmItgcView;
